import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Loader2, Puzzle, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { AppMainHeader } from "@/components/AppMainHeader";
import { MobileBackBar } from "@/components/MobileBackBar";
import { useIsMobile } from "@/hooks/use-mobile";
import { getDotPatternStyle } from "@/components/AppBackground";
import EmojiPickerPopover from "@/components/onboarding/EmojiPickerPopover";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import {
  getArtefactos,
  getArtefacto,
  updateArtefacto,
  deleteArtefacto,
  getArtefactoPresets,
  createArtefactoPreset,
  updateArtefactoPreset,
  deleteArtefactoPreset,
  getArtefactoKv,
  setArtefactoKv,
} from "@/lib/api";

const DEFAULT_EMOJI = "🧩";

/**
 * Puente que se inyecta al inicio del HTML del artefacto para que su JS pueda guardar/leer
 * "ajustes" (presets) a través del CRM sin que el iframe tenga acceso directo a su API/token
 * (sandbox sin allow-same-origin). Comunicación por postMessage, verificada por identidad de
 * la ventana (event.source), no por origin (los iframes con srcDoc tienen origin "null").
 */
const ARTEFACTO_SDK_SCRIPT = `<script>
(function(){
  if (window.ArtefactoSDK) return;
  var pending = {};
  var seq = 0;
  window.addEventListener('message', function(ev){
    var d = ev.data;
    if (!d || d.source !== 'artefacto-sdk-response') return;
    var p = pending[d.requestId];
    if (!p) return;
    delete pending[d.requestId];
    if (d.ok) p.resolve(d.result); else p.reject(new Error(d.error || 'Error'));
  });
  function call(type, payload){
    return new Promise(function(resolve, reject){
      if (window.parent === window) { reject(new Error('ArtefactoSDK no disponible fuera del CRM')); return; }
      var requestId = 'r' + (++seq) + '_' + Date.now();
      var timer = setTimeout(function(){
        if (pending[requestId]) { delete pending[requestId]; reject(new Error('Tiempo de espera agotado')); }
      }, 8000);
      pending[requestId] = {
        resolve: function(v){ clearTimeout(timer); resolve(v); },
        reject: function(e){ clearTimeout(timer); reject(e); },
      };
      window.parent.postMessage({ source: 'artefacto-sdk-request', type: type, requestId: requestId, payload: payload || {} }, '*');
    });
  }
  var available = window.parent !== window;
  window.ArtefactoSDK = {
    available: available,
    listPresets: function(){ return call('listPresets'); },
    savePreset: function(name, data){ return call('savePreset', { name: name, data: data }); },
    renamePreset: function(id, name){ return call('updatePreset', { id: id, name: name }); },
    updatePresetData: function(id, data){ return call('updatePreset', { id: id, data: data }); },
    deletePreset: function(id){ return call('deletePreset', { id: id }); },
  };
  // Compatible con artefactos que ya usan window.storage.get/set (patrón de los artifacts de
  // claude.ai): si no está disponible (archivo abierto fuera del CRM), se deja sin definir para
  // que el propio artefacto recurra a su fallback de localStorage.
  if (available) {
    window.storage = {
      get: function(key){ return call('kvGet', { key: key }); },
      set: function(key, value){ return call('kvSet', { key: key, value: value }); },
    };
  }
})();
<\/script>`;

/**
 * Inserta el script del puente en el sitio correcto según cómo venga el HTML del artefacto:
 * - Si trae <head>, se inyecta justo después de abrirlo (para que quede definido antes que
 *   cualquier otro script del propio documento, sin interferir con un <!DOCTYPE> que debe ir
 *   primero).
 * - Si trae <html> pero no <head>, después de abrir <html>.
 * - Si es un fragmento suelto (sin doctype/html), se antepone tal cual.
 */
function injectArtefactoSdk(html) {
  const src = html || "";
  const headMatch = src.match(/<head[^>]*>/i);
  if (headMatch) {
    const idx = headMatch.index + headMatch[0].length;
    return src.slice(0, idx) + ARTEFACTO_SDK_SCRIPT + src.slice(idx);
  }
  const htmlMatch = src.match(/<html[^>]*>/i);
  if (htmlMatch) {
    const idx = htmlMatch.index + htmlMatch[0].length;
    return src.slice(0, idx) + ARTEFACTO_SDK_SCRIPT + src.slice(idx);
  }
  return ARTEFACTO_SDK_SCRIPT + src;
}

function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFecha(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${d.getFullYear()}`;
}

export default function ArtefactosPage() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const settings = useSettings();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  // Móvil: lista y previsualización no conviven; en escritorio se ignora
  const [mobileShowList, setMobileShowList] = useState(true);
  const isMobile = useIsMobile();
  const [content, setContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);

  const [menuOpenId, setMenuOpenId] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const iframeRef = useRef(null);
  const selectedIdRef = useRef(null);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  // Puente postMessage: el artefacto (iframe sandboxed, sin allow-same-origin) pide guardar/leer
  // sus "ajustes" y aquí se resuelve contra la API con el token del usuario del CRM.
  useEffect(() => {
    const handleMessage = async (event) => {
      const iframeEl = iframeRef.current;
      if (!iframeEl || event.source !== iframeEl.contentWindow) return;
      const msg = event.data;
      if (!msg || msg.source !== "artefacto-sdk-request") return;
      const respond = (ok, resultOrError) => {
        event.source.postMessage(
          ok
            ? { source: "artefacto-sdk-response", requestId: msg.requestId, ok: true, result: resultOrError }
            : { source: "artefacto-sdk-response", requestId: msg.requestId, ok: false, error: String(resultOrError) },
          "*"
        );
      };
      const artefactoId = selectedIdRef.current;
      if (!artefactoId) {
        respond(false, "No hay artefacto seleccionado");
        return;
      }
      try {
        let result;
        const payload = msg.payload || {};
        switch (msg.type) {
          case "listPresets": {
            const data = await getArtefactoPresets(artefactoId);
            result = data.presets || [];
            break;
          }
          case "savePreset":
            result = await createArtefactoPreset(artefactoId, payload.name, payload.data);
            break;
          case "updatePreset": {
            const patch = {};
            if (payload.name !== undefined) patch.name = payload.name;
            if (payload.data !== undefined) patch.data = payload.data;
            result = await updateArtefactoPreset(artefactoId, payload.id, patch);
            break;
          }
          case "deletePreset":
            result = await deleteArtefactoPreset(artefactoId, payload.id);
            break;
          case "kvGet":
            try {
              const data = await getArtefactoKv(artefactoId, payload.key);
              result = { value: data.value };
            } catch (err) {
              if (err.response?.status === 404) {
                result = null;
                break;
              }
              throw err;
            }
            break;
          case "kvSet":
            result = await setArtefactoKv(artefactoId, payload.key, payload.value);
            break;
          default:
            respond(false, `Tipo de mensaje desconocido: ${msg.type}`);
            return;
        }
        respond(true, result);
      } catch (err) {
        respond(false, err.response?.data?.detail || err.message || "Error");
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.permissions?.artefactos === false) navigate("/candidatos", { replace: true });
  }, [authLoading, user, navigate]);

  const loadArtefactos = useCallback(() => {
    if (!user || user.permissions?.artefactos === false) return;
    setLoading(true);
    getArtefactos()
      .then((data) => {
        const list = data.artefactos || [];
        setItems(list);
        setSelectedId((prev) => prev || list[0]?.id || null);
      })
      .catch(() => {
        toast.error("Error al cargar artefactos");
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    loadArtefactos();
  }, [loadArtefactos]);

  useEffect(() => {
    if (!selectedId) {
      setContent(null);
      return;
    }
    let cancelled = false;
    setLoadingContent(true);
    setContent(null);
    getArtefacto(selectedId)
      .then((data) => {
        if (!cancelled) setContent(data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Error al cargar el artefacto");
      })
      .finally(() => {
        if (!cancelled) setLoadingContent(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const handleSelectEmoji = async (id, emoji) => {
    setItems((prev) => prev.map((a) => (a.id === id ? { ...a, emoji } : a)));
    try {
      await updateArtefacto(id, { emoji });
    } catch (_) {
      toast.error("No se pudo guardar el emoji");
      loadArtefactos();
    }
  };

  const openRename = (item) => {
    setRenameTarget(item);
    setRenameValue(item.name || "");
  };

  const handleRename = async () => {
    const name = renameValue.trim();
    if (!name || !renameTarget) {
      toast.error("Indica un nombre para el artefacto");
      return;
    }
    setRenaming(true);
    try {
      await updateArtefacto(renameTarget.id, { name });
      setItems((prev) => prev.map((a) => (a.id === renameTarget.id ? { ...a, name } : a)));
      toast.success("Artefacto renombrado");
      setRenameTarget(null);
      setRenameValue("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo renombrar el artefacto");
    } finally {
      setRenaming(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await deleteArtefacto(deleteTarget.id);
      toast.success("Artefacto eliminado");
      const removedId = deleteTarget.id;
      setDeleteTarget(null);
      if (selectedId === removedId) setSelectedId(null);
      await loadArtefactos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar el artefacto");
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading || !user || user.permissions?.artefactos === false) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const current = items.find((a) => a.id === selectedId) || null;
  const dotStyle = getDotPatternStyle(settings);

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      <AppMainHeader title="Artefactos" titleSuffix={current && !(isMobile && mobileShowList) ? ` / ${current.name}` : ""} titleAs="h5" />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar: artefactos en cajas */}
        <aside className={`${mobileShowList ? "flex" : "hidden md:flex"} w-full md:w-80 shrink-0 flex-col md:border-r border-gray-300 bg-background overflow-hidden`}>
          <div className="p-4 shrink-0 border-b border-gray-200">
            <p className="text-sm font-semibold">Artefactos</p>
            <p className="text-xs text-muted-foreground">Mini-herramientas HTML (p. ej. hechas en Claude).</p>
          </div>
          <div className="p-4 flex-1 overflow-auto mobile-nav-scroll">
            {loading && items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" aria-hidden />
                <span>Cargando…</span>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 px-4 text-center text-sm text-muted-foreground">
                <Puzzle className="w-8 h-8 text-muted-foreground/50" strokeWidth={1.5} aria-hidden />
                <span>Aún no hay artefactos.</span>
              </div>
            ) : (
              <ul className="space-y-2" role="list">
                {items.map((a) => {
                  const isActive = selectedId === a.id;
                  const menuOpen = menuOpenId === a.id;
                  return (
                    <li key={a.id}>
                      <Card
                        className={`group transition-colors shadow-none hover:bg-muted/50 ${isActive ? "bg-muted/40" : ""}`}
                        onContextMenu={(e) => { e.preventDefault(); setMenuOpenId(a.id); }}
                      >
                        <CardContent className="p-3 flex items-center gap-3">
                          <EmojiPickerPopover value={a.emoji} onSelect={(emoji) => handleSelectEmoji(a.id, emoji)}>
                            <button
                              type="button"
                              className="w-11 h-11 shrink-0 rounded-lg bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              title="Cambiar icono"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="text-2xl leading-none select-none" aria-hidden style={{ textShadow: "rgba(0,0,0,0.3) 0px 1px 2px" }}>
                                {a.emoji || DEFAULT_EMOJI}
                              </span>
                            </button>
                          </EmojiPickerPopover>
                          <button
                            type="button"
                            className="flex-1 min-w-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
                            onClick={() => { setSelectedId(a.id); setMobileShowList(false); }}
                          >
                            <p className="text-sm font-medium truncate">{a.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatSize(a.size)} · {formatFecha(a.updated_at || a.created_at)}
                            </p>
                          </button>
                          <DropdownMenu open={menuOpen} onOpenChange={(open) => setMenuOpenId(open ? a.id : null)}>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={`h-10 w-10 md:h-8 md:w-8 shrink-0 transition-opacity ${menuOpen ? "opacity-100" : "md:opacity-0 md:group-hover:opacity-100"}`}
                                onClick={(e) => e.stopPropagation()}
                                aria-label="Acciones del artefacto"
                              >
                                <MoreVertical className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem
                                onSelect={(e) => {
                                  e.preventDefault();
                                  openRename(a);
                                }}
                              >
                                Editar nombre
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={(e) => {
                                  e.preventDefault();
                                  setDeleteTarget(a);
                                }}
                              >
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </CardContent>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Panel: previsualización del artefacto seleccionado */}
        <div className={`${mobileShowList ? "hidden md:flex" : "flex"} flex-1 min-w-0 flex-col overflow-hidden`} style={dotStyle}>
          <MobileBackBar onBack={() => setMobileShowList(true)} label="Artefactos" />
          {!current ? (
            <div className="h-full flex items-center justify-center px-6 text-center text-muted-foreground">
              <p>Selecciona un artefacto de la izquierda.</p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 relative bg-white mobile-nav-scroll">
              {loadingContent ? (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="w-6 h-6 animate-spin" aria-hidden />
                </div>
              ) : content?.html ? (
                <iframe
                  key={current.id}
                  ref={iframeRef}
                  title={current.name}
                  srcDoc={injectArtefactoSdk(content.html)}
                  sandbox="allow-scripts allow-downloads allow-modals allow-forms allow-popups"
                  className="w-full h-full border-0"
                />
              ) : null}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={!!renameTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRenameTarget(null);
            setRenameValue("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar artefacto</DialogTitle>
            <DialogDescription>Cambia el nombre visible del artefacto en la lista.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="artefacto-rename">Nombre</Label>
            <Input
              id="artefacto-rename"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleRename(); }}
              placeholder="Nombre del artefacto"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>Cancelar</Button>
            <Button onClick={handleRename} disabled={renaming}>
              {renaming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar artefacto?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará «{deleteTarget?.name}» permanentemente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Eliminar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
